import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

import {
  EXPORTS, IMPORTS,
  SectionContainer, SectionHeader, SectionWrapper,
} from 'src/enosikit/components/Chart/components/chartConstants';
import {
  getDirectionLabel, getMeterChartToolipLabel, getTooltipFormatFunc,
} from 'src/enosikit/components/Chart/helpers/tooltip';
import { DATA_AGGREGATE_BY_PROPERTY } from 'src/util/constants';
import { useIntlCurrency } from 'src/util/i18n/currency';

/**
 * Renders an individual meter data section with its title, identifier, formatted value, and flags.
 * @param {object} props Component props.
 * @param {object} props.tooltipData Meter tooltip data.
 * @param {string} props.dirLabel Import or export label.
 * @param {string} props.unitLabel Unit display label.
 * @param {Function} props.tooltipFormatFunc Value formatter.
 * @returns {React.ReactElement} Meter tooltip section.
 */
export function MeterTooltipSection({
  tooltipData = {}, dirLabel, unitLabel, tooltipFormatFunc,
}) {
  const {
    flags, value, title, identifier,
  } = tooltipData;

  return (
    <SectionWrapper>
      <SectionHeader>
        {getMeterChartToolipLabel(dirLabel, { title, identifier })}
      </SectionHeader>
      <div>
        {tooltipFormatFunc(value, unitLabel)}
      </div>
      {flags?.length > 0 && (
        <SectionContainer>
          {flags.map((flag) => flag.identifier).join(', ')}
        </SectionContainer>
      )}
    </SectionWrapper>
  );
}

MeterTooltipSection.propTypes = {
  dirLabel: PropTypes.string.isRequired,
  tooltipData: PropTypes.shape({}),
  tooltipFormatFunc: PropTypes.func.isRequired,
  unitLabel: PropTypes.string.isRequired,
};

/**
 * Renders the meter data for one direction, supporting single-meter and property aggregations.
 * @param {object} props Component props.
 * @param {object} props.tooltipData Meter tooltip data.
 * @param {string} props.dirLabel Import or export label.
 * @param {string} props.unitLabel Unit display label.
 * @param {boolean} props.aggByProperty Whether data is aggregated by property.
 * @param {Function} props.tooltipFormatFunc Value formatter.
 * @returns {React.ReactElement|null} Meter tooltip content.
 */
export function MeterTooltipDirectionGroup({
  tooltipData = null, dirLabel, unitLabel, aggByProperty, tooltipFormatFunc,
}) {
  if (!tooltipData) {
    return null;
  }

  if (!aggByProperty) {
    return (
      <MeterTooltipSection
        dirLabel={dirLabel}
        tooltipData={tooltipData}
        tooltipFormatFunc={tooltipFormatFunc}
        unitLabel={unitLabel}
      />
    );
  }

  const sortedMeterIds = Object.keys(tooltipData)
    .sort((a, b) => (tooltipData[b]?.value ?? 0) - (tooltipData[a]?.value ?? 0));

  return (
    <>
      {sortedMeterIds.map((meterId) => (
        <MeterTooltipSection
          key={meterId}
          dirLabel={dirLabel}
          tooltipData={tooltipData[meterId]}
          tooltipFormatFunc={tooltipFormatFunc}
          unitLabel={unitLabel}
        />
      ))}
    </>
  );
}

MeterTooltipDirectionGroup.propTypes = {
  aggByProperty: PropTypes.bool.isRequired,
  dirLabel: PropTypes.string.isRequired,
  tooltipData: PropTypes.shape({}),
  tooltipFormatFunc: PropTypes.func.isRequired,
  unitLabel: PropTypes.string.isRequired,
};

/**
 * Primary meter tooltip entry point that splits consumed imports and generated exports into groups.
 * @param {object} props Component props.
 * @param {object} props.tooltipData Meter tooltip data.
 * @param {string} props.unitLabel Unit display label.
 * @param {object} props.chartView Chart aggregation and grouping options.
 * @param {string} props.unit Chart unit.
 * @returns {React.ReactElement|null} Meter tooltip content.
 */
export function MeterChartTooltip({
  tooltipData = null, unitLabel = null, chartView = null, unit = null,
}) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  if (!tooltipData || !chartView) {
    return null;
  }

  const { tooltipConsumed, tooltipGenerated } = tooltipData;
  if (!tooltipConsumed && !tooltipGenerated) {
    return null;
  }

  const aggByProperty = chartView.aggregateBy === DATA_AGGREGATE_BY_PROPERTY;
  const tooltipFormatFunc = getTooltipFormatFunc(unit, undefined, intlCurrency);

  return (
    <>
      {tooltipConsumed && (
        <MeterTooltipDirectionGroup
          aggByProperty={aggByProperty}
          dirLabel={getDirectionLabel(IMPORTS, intl)}
          tooltipData={tooltipConsumed}
          tooltipFormatFunc={tooltipFormatFunc}
          unitLabel={unitLabel}
        />
      )}
      {tooltipGenerated && (
        <MeterTooltipDirectionGroup
          aggByProperty={aggByProperty}
          dirLabel={getDirectionLabel(EXPORTS, intl)}
          tooltipData={tooltipGenerated}
          tooltipFormatFunc={tooltipFormatFunc}
          unitLabel={unitLabel}
        />
      )}
    </>
  );
}

MeterChartTooltip.propTypes = {
  chartView: PropTypes.shape({}),
  tooltipData: PropTypes.shape({}),
  unit: PropTypes.string,
  unitLabel: PropTypes.string,
};
